/**
 * Weergave van verlofsaldi.
 *
 * Eén plek, omdat een saldo op vier schermen staat en er precies één regel is
 * die overal moet gelden: een ONBEKEND saldo (`null`) is geen saldo van nul.
 * Nmbrs is de enige bron van het getal; lukt die call niet, dan hoort de
 * gebruiker "onbekend" te zien. Tot 2026-07-29 werd een onbekend of niet te
 * berekenen saldo stilletjes 0 — daardoor stond maandenlang iedereen op een
 * negatief saldo zonder dat iemand doorhad dat de bron nooit was geraadpleegd.
 */

/** "32 uur", of "onbekend" als Nmbrs het saldo niet gaf. */
export function formatSaldo(value: number | null): string {
  return value === null ? 'onbekend' : `${value.toFixed(1)} uur`;
}

/** Alleen het getal, zonder eenheid — voor tegels die "uur" er los onder zetten. */
export function formatSaldoGetal(value: number | null): string {
  return value === null ? 'onbekend' : String(value);
}

/**
 * Uren omgerekend naar hele en halve dagen.
 *
 * `urenPerDag` komt uit het Nmbrs-rooster, niet uit `weekuren / 5`: wie 24 uur
 * over 3 dagen werkt heeft vrije dagen van 8 uur, over 4 dagen van 6 uur. Een
 * vast deeltal geeft die twee mensen hetzelfde onjuiste antwoord.
 *
 * `null` zodra het rooster ontbreekt — dan toont het scherm alleen uren, want
 * een dagental zonder rooster zou een aanname zijn.
 */
export function urenNaarDagen(uren: number | null, urenPerDag: number | null): number | null {
  if (uren === null || urenPerDag === null || urenPerDag <= 0) return null;
  // Halve dagen: fijner dan hele dagen (een halve vrije dag bestaat), maar
  // niet zó fijn dat "3,7 dagen" suggereert dat je in tienden kunt opnemen.
  return Math.round((uren / urenPerDag) * 2) / 2;
}

/**
 * Het hoofdgetal van de jaarruimte: dagen als dat kan, anders uren.
 *
 * Bij een wisselend rooster (week 1 ≠ week 2) is het dagental een gemiddelde,
 * en dat zegt het label dan ook met "±". De uren blijven wél exact.
 */
export function formatJaarruimte(
  uren: number | null,
  urenPerDag: number | null,
  wisselendRooster: boolean = false
): string {
  if (uren === null) return 'onbekend';

  const dagen = urenNaarDagen(uren, urenPerDag);
  if (dagen === null) return `${uren} uur`;

  const prefix = wisselendRooster ? '± ' : '';
  return `${prefix}${dagen} ${dagen === 1 ? 'dag' : 'dagen'}`;
}

/** De kleine onderregel bij het hoofdgetal: de exacte uren plus de rekenbasis. */
export function formatJaarruimteToelichting(
  uren: number | null,
  vakantiedagenPerJaar: number | null
): string | null {
  if (uren === null) return null;
  const basis =
    vakantiedagenPerJaar === null
      ? ''
      : ` · op basis van ${vakantiedagenPerJaar} vakantiedagen per jaar`;
  return `${uren} uur${basis}`;
}

/** "32 uur (4 dagen)" voor de kleine onderregel met de huidige stand. */
export function formatSaldoMetDagen(uren: number | null, urenPerDag: number | null): string {
  if (uren === null) return 'onbekend';
  const dagen = urenNaarDagen(uren, urenPerDag);
  return dagen === null ? `${uren} uur` : `${uren} uur (${dagen} ${dagen === 1 ? 'dag' : 'dagen'})`;
}

/**
 * "2 dgn (16 uur)" — dagen voorop, uren erachter tussen haakjes.
 *
 * De vorm die in overzichten gevraagd is (Friso, 2026-07-30): je denkt in
 * dagen, maar de uren moeten erbij blijven staan omdat de loonstrook en Nmbrs
 * daarin rekenen. Zonder rooster valt het terug op alleen uren — een dagental
 * zonder de werkelijke dagduur zou een aanname zijn.
 *
 * `null` blijft "onbekend"; nooit 0.
 */
export function formatDagenMetUren(
  uren: number | null,
  urenPerDag: number | null,
  wisselendRooster: boolean = false,
  metUren: boolean = true,
  metEenheid: boolean = true
): string {
  if (uren === null) return 'onbekend';

  const dagen = urenNaarDagen(uren, urenPerDag);
  // Zonder rooster is er geen dagental; dan zijn de uren het enige eerlijke
  // antwoord, ook in een weergave die verder alleen dagen toont.
  if (dagen === null) return `${uren.toFixed(1)} uur`;

  // In een tabel staat de eenheid in de kolomkop; hem in elke cel herhalen is
  // precies de ruis waar het overzicht aan onderdoor ging.
  const eenheid = metEenheid ? ` ${dagen === 1 || dagen === -1 ? 'dag' : 'dgn'}` : '';
  // Altijd één decimaal, ook bij een rond getal: anders lopen de kolommen
  // rafelig af (25 naast 17.5) en moet het oog per rij opnieuw uitlijnen.
  // Het wisselend-rooster-voorbehoud stond hier als "±" vóór het getal en
  // brak diezelfde uitlijning; dat zit nu in de tooltip van de cel.
  void wisselendRooster;
  return `${dagen.toFixed(1)}${eenheid}${metUren ? ` (${uren.toFixed(1)} uur)` : ''}`;
}
